/**
 * PwwLogoIcon — single source of truth for the PWWCapital brand mark.
 *
 * Usage:
 *   <PwwLogoIcon size={32} />                 // auto-detects theme
 *   <PwwLogoIcon size={32} variant="dark" />  // white icon on navy bg
 *   <PwwLogoIcon size={32} variant="light" /> // blue icon on white bg
 *   <PwwLogoIcon size={32} variant="flat" />  // icon only, no background rect
 *
 * Rules (non-negotiable):
 *   - Do NOT change colors, proportions, or path data per-page.
 *   - Only allowed prop changes: size, variant.
 *   - Always import from this file — never inline SVG markup elsewhere.
 */

import React from 'react';

// ── Brand tokens ─────────────────────────────────────────────────────────────
const NAVY       = '#0A3D62';   // background (dark variant)
const PRIMARY    = '#0A4D9C';   // primary blue
const ACCENT     = '#1565C8';   // lighter facet
const WHITE      = '#FFFFFF';

/**
 * Geometric emblem: four interlocking diamond facets arranged as a symmetric
 * crest — suggests security, structure, and multi-directional flow.
 * Works as favicon, app icon, card mark, and sidebar brand.
 */
export function PwwLogoIcon({ size = 32, variant, className = '', style = {} }) {
  // Resolve variant from context if not explicitly passed.
  // variant: 'dark' | 'light' | 'flat'
  const resolvedVariant = variant || 'light';

  const showBg    = resolvedVariant !== 'flat';
  const isDark    = resolvedVariant === 'dark';
  const bgFill    = isDark ? NAVY   : WHITE;
  const iconColor = isDark ? WHITE  : PRIMARY;
  const facetColor = isDark ? 'rgba(255,255,255,0.55)' : ACCENT;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="PWWCapital"
      className={className}
      style={{ flexShrink: 0, display: 'inline-block', ...style }}
    >
      {/* Background — rounded square (omit in flat mode) */}
      {showBg && (
        <rect x="0" y="0" width="64" height="64" rx="12" fill={bgFill} />
      )}

      {/*
        Emblem: a symmetric four-facet crest.

        The four diamonds share a common centre at (32,32).
        Top facet    — primary (anchors the mark upward → stability)
        Right facet  — accent  (directional flow → right = growth)
        Bottom facet — primary (mirror of top → balance)
        Left facet   — accent  (mirror of right → interconnection)

        Grid: 8-unit steps on 64px canvas.
        All coordinates are whole numbers — no subpixel drift.
      */}
      <g>
        {/* Top facet */}
        <path
          d="M32 10 L44 22 L32 28 L20 22 Z"
          fill={iconColor}
        />
        {/* Right facet */}
        <path
          d="M44 22 L54 32 L44 42 L32 36 Z"
          fill={facetColor}
        />
        {/* Bottom facet */}
        <path
          d="M32 36 L44 42 L32 54 L20 42 Z"
          fill={iconColor}
        />
        {/* Left facet */}
        <path
          d="M20 22 L32 28 L32 36 L10 32 Z"
          fill={facetColor}
        />
        {/* Centre diamond — anchors the four facets */}
        <path
          d="M32 28 L44 32 L32 36 L20 32 Z"
          fill={isDark ? 'rgba(255,255,255,0.25)' : 'rgba(10,77,156,0.18)'}
        />
      </g>
    </svg>
  );
}

/**
 * PwwLogoFull — icon + wordmark, used in auth headers and public pages.
 * Never use this inside the compact sidebar — use PwwLogoIcon there.
 */
export function PwwLogoFull({ size = 36, variant = 'light', className = '', style = {} }) {
  const textColor = variant === 'dark' ? WHITE : NAVY;
  const subColor  = variant === 'dark' ? 'rgba(255,255,255,0.55)' : '#4B6B8A';

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: Math.round(size * 0.3),
        textDecoration: 'none',
        ...style,
      }}
    >
      <PwwLogoIcon size={size} variant={variant} />
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
        <span
          style={{
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 700,
            fontSize: Math.round(size * 0.48),
            letterSpacing: '-0.02em',
            color: textColor,
          }}
        >
          PWWCapital
        </span>
        <span
          style={{
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 400,
            fontSize: Math.round(size * 0.26),
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: subColor,
            marginTop: 1,
          }}
        >
          Institutional Banking
        </span>
      </span>
    </span>
  );
}

export default PwwLogoIcon;
